"use client";

import { observer } from "mobx-react-lite";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import Dropdown from "../../shared/components/dropdown/Dropdown";
import ErrorBoundary from "../../shared/components/error-boundary/ErrorBoundary";

import { useAppContext } from "../../shared/functions/Context";
import { dataFormat } from "../../shared/functions/Directives";
import {
  MAIL_EMAIL,
  MAIL_MIDTERM_RATINGS_ME,
} from "../../shared/functions/mailMessages";
import showModalFromId from "../../shared/functions/ModalShow";
import useAutoSave from "../../shared/hooks/useAutoSave";
import {
  ALL_TAB,
  fullPerspectiveName,
} from "../../shared/interfaces/IPerspectiveTabs";
import type Measure from "../../shared/models/Measure";
import type Objective from "../../shared/models/Objective";
import type { IScorecardMetadata } from "../../shared/models/ScorecardMetadata";
import EmptyError from "../admin-settings/EmptyError";
import MODAL_NAMES from "../dialogs/ModalName";

import Rating from "../shared/components/rating/Rating";

import Toolbar from "../shared/components/toolbar/Toolbar";
import {
  rateColor,
  semester1EmpRating,
  semester1FinalRating,
  semester1SuperRating,
} from "../shared/functions/Scorecard";
import NoPerformanceData from "./NoPerformanceData";
import NoMeasures from "./no-measures/NoMeasures";
import type { IUser } from "../../shared/models/User";

import { useParams } from "react-router-dom";

interface IMidtermReviewMeasureTableItemProps {
  measure: Measure;
  canUpdate: boolean;
  midtermRated: boolean;
}

const MeasureTableItem = observer(
  (props: IMidtermReviewMeasureTableItemProps) => {
    const { api, store, ui } = useAppContext();
    const measure = props.measure.asJson;
    const { autoSave } = useAutoSave({ timeout: 2000 });

    const dataType = measure.dataType;
    const dataSymbol = measure.dataSymbol;

    const [ratingCss, setRatingCss] = useState("");

    const readMeasureComment = () => {
      store.measure.select(measure);
      showModalFromId(MODAL_NAMES.EXECUTION.MEASURE_COMMENTS_MIDTERM_MODAL);
    };

    const handleDispute = () => {
      store.measure.select(measure);
      showModalFromId(MODAL_NAMES.EXECUTION.SCORECARD_DISPUTE_Q2_MODAL);
    };

    const measureReadOnly = () => {
      store.measure.select(measure);
      showModalFromId(MODAL_NAMES.EXECUTION.MEASURE_READ_ONLY_MODAL);
    };

    useEffect(() => {
      if (measure.finalRating) {
        const rateCss = rateColor(
          Number(measure.finalRating),
          measure.isUpdated
        );
        setRatingCss(rateCss);
      } else if (measure.supervisorRating) {
        const ratingCss = rateColor(
          Number(measure.supervisorRating),
          measure.isUpdated
        );
        setRatingCss(ratingCss);
      } else {
        const ratingCss = rateColor(measure.autoRating ?? 0, measure.isUpdated);
        setRatingCss(ratingCss);
      }
    }, [measure, ratingCss]);

    const disputeExists = measure.isDispute === true;

  const isResolved = Boolean(measure.isResolved)
  // max:I reused status colours so employees instantly see if HR resolved their dispute
  const disputeContainerClass = isResolved
    ? "bg-green-50 border-l-4 border-[#4bb543] rounded-lg p-4 mt-3"
    : "bg-[#fff3cd] border-l-4 border-[#faa05a] rounded-lg p-4 mt-3"
  const statusBadgeClass = isResolved
    ? "inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#dff5e5] text-[#2b6a2f] text-xs font-semibold"
    : "inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#ffe8cc] text-[#a35c22] text-xs font-semibold"

  return (
    <ErrorBoundary>
      <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200 mb-3">
        <div className="flex items-start gap-4">
          {/* Status Indicator */}
          <div
            className={`w-1 h-full rounded-full ${ratingCss === "rating-1" ? "bg-[#dc3545]" : ratingCss === "rating-2" ? "bg-[#faa05a]" : ratingCss === "rating-3" ? "bg-[#e7a637]" : ratingCss === "rating-4" ? "bg-[#84cbe9]" : "bg-[#4bb543]"}`}
          />

            <div className="flex-1">
              {/* Measure Description */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="text-base font-semibold text-[#3f6a8e] mb-1">
                    {measure.description}
                  </h4>
                  <button
                    title="Comments"
                    className="text-[#84cbe9] hover:text-[#3f6a8e] transition-colors text-sm flex items-center gap-1"
                    onClick={readMeasureComment}
                  >
                    <span data-uk-icon="icon: comments; ratio: 0.7" />
                    View Comments
                  </button>
                </div>

              {/* Dispute Button */}
              <button
                title={disputeExists ? "Edit Dispute" : "Dispute Rating"}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${disputeExists
                    ? "bg-[#faa05a] hover:bg-[#e7a637] text-white"
                    : "bg-[#dc3545] hover:bg-[#c82333] text-white"
                  }`}
                onClick={handleDispute}
              >
                {disputeExists ? "Edit Dispute" : "Dispute Rating"}
              </button>
            </div>

              {/* Ratings Grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Rating 1</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {dataFormat(dataType, measure.rating1, dataSymbol)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Rating 2</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {dataFormat(dataType, measure.rating2, dataSymbol)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Rating 3</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {dataFormat(dataType, measure.rating3, dataSymbol)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Rating 4</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {dataFormat(dataType, measure.rating4, dataSymbol)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Rating 5</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {dataFormat(dataType, measure.rating5, dataSymbol)}
                  </p>
                </div>
              </div>

              {/* Target and Progress */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-[#3f6a8e] mb-1">Target</p>
                  <p className="text-sm font-semibold text-[#3f6a8e]">
                    {dataFormat(dataType, measure.annualTarget, dataSymbol)}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-[#3f6a8e] mb-1">Progress</p>
                  <p className="text-sm font-semibold text-[#3f6a8e]">
                    {dataFormat(dataType, measure.annualActual, dataSymbol)}
                  </p>
                </div>
                <div
                  className={`rounded-lg p-3 ${
                    ratingCss === "rating-1"
                      ? "bg-red-50"
                      : ratingCss === "rating-2"
                      ? "bg-orange-50"
                      : ratingCss === "rating-3"
                      ? "bg-yellow-50"
                      : ratingCss === "rating-4"
                      ? "bg-blue-50"
                      : "bg-green-50"
                  }`}
                >
                  <p className="text-xs text-gray-600 mb-1">E-Rating</p>
                  <p
                    className={`text-sm font-semibold ${
                      ratingCss === "rating-1"
                        ? "text-[#dc3545]"
                        : ratingCss === "rating-2"
                        ? "text-[#faa05a]"
                        : ratingCss === "rating-3"
                        ? "text-[#e7a637]"
                        : ratingCss === "rating-4"
                        ? "text-[#84cbe9]"
                        : "text-[#4bb543]"
                    }`}
                  >
                    {measure.autoRating}
                  </p>
                </div>
                <div
                  className={`rounded-lg p-3 ${
                    ratingCss === "rating-1"
                      ? "bg-red-50"
                      : ratingCss === "rating-2"
                      ? "bg-orange-50"
                      : ratingCss === "rating-3"
                      ? "bg-yellow-50"
                      : ratingCss === "rating-4"
                      ? "bg-blue-50"
                      : "bg-green-50"
                  }`}
                >
                  <p className="text-xs text-gray-600 mb-1">S-Rating</p>
                  <p
                    className={`text-sm font-semibold ${
                      ratingCss === "rating-1"
                        ? "text-[#dc3545]"
                        : ratingCss === "rating-2"
                        ? "text-[#faa05a]"
                        : ratingCss === "rating-3"
                        ? "text-[#e7a637]"
                        : ratingCss === "rating-4"
                        ? "text-[#84cbe9]"
                        : "text-[#4bb543]"
                    }`}
                  >
                    {measure.supervisorRating}
                  </p>
                </div>
                <div
                  className={`rounded-lg p-3 ${
                    ratingCss === "rating-1"
                      ? "bg-red-50"
                      : ratingCss === "rating-2"
                      ? "bg-orange-50"
                      : ratingCss === "rating-3"
                      ? "bg-yellow-50"
                      : ratingCss === "rating-4"
                      ? "bg-blue-50"
                      : "bg-green-50"
                  }`}
                >
                  <p className="text-xs text-gray-600 mb-1">F-Rating</p>
                  <p
                    className={`text-sm font-semibold ${
                      ratingCss === "rating-1"
                        ? "text-[#dc3545]"
                        : ratingCss === "rating-2"
                        ? "text-[#faa05a]"
                        : ratingCss === "rating-3"
                        ? "text-[#e7a637]"
                        : ratingCss === "rating-4"
                        ? "text-[#84cbe9]"
                        : "text-[#4bb543]"
                    }`}
                  >
                    {measure.finalRating}
                  </p>
                </div>
              </div>

            {/* Dispute Information */}
            {disputeExists && (
              <div className={disputeContainerClass}>
                <div className="flex items-start gap-2">
                  <span
                    data-uk-icon="icon: warning; ratio: 0.8"
                    className={`mt-0.5 ${isResolved ? "text-[#4bb543]" : "text-[#e7a637]"}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-[#856404]">Dispute Details</p>
                      <span className={statusBadgeClass}>
                        {isResolved ? "Resolved" : "Awaiting Resolution"}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="font-medium text-gray-700">Reason:</span>{" "}
                        <span className="text-gray-900">{measure.disputeReason}</span>
                      </p>
                      {measure.preferredRating && (
                        <p>
                          <span className="font-medium text-gray-700">Preferred Rating:</span>{" "}
                          <span className="text-gray-900">{measure.preferredRating}</span>
                        </p>
                      )}
                      {measure.disputeType && (
                        <p>
                          <span className="font-medium text-gray-700">Type:</span>{" "}
                          <span className="text-gray-900">{measure.disputeType}</span>
                        </p>
                      )}
                      {measure.additionalComments && (
                        <p>
                          <span className="font-medium text-gray-700">Additional Comments:</span>{" "}
                          <span className="text-gray-900">{measure.additionalComments}</span>
                        </p>
                      )}
                      {isResolved && measure.supervisorDisputeResolution && (
                        <p className="text-gray-900">
                          <span className="font-medium text-gray-700">Supervisor Resolution:</span>{" "}
                          {measure.supervisorDisputeResolution}
                        </p>
                      )}
                      {isResolved && measure.employeeDisputeResolution && (
                        <p className="text-gray-900">
                          <span className="font-medium text-gray-700">Employee Resolution:</span>{" "}
                          {measure.employeeDisputeResolution}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  )
})

interface IMidtermReviewProps {
  measures: Measure[];
  canUpdate: boolean;
  midtermRated: boolean;
}
const MeasureTable = observer((props: IMidtermReviewProps) => {
  const { measures, canUpdate, midtermRated } = props;

  return (
    <ErrorBoundary>
      <div className="space-y-3">
        {measures.length !== 0 && (
          <div className="space-y-3">
            {measures.map((measure) => (
              <ErrorBoundary key={measure.asJson.id}>
                <MeasureTableItem
                  measure={measure}
                  canUpdate={canUpdate}
                  midtermRated={midtermRated}
                />
              </ErrorBoundary>
            ))}
          </div>
        )}
        {measures.length === 0 && <NoMeasures />}
      </div>
    </ErrorBoundary>
  );
});

interface ObjectiveItemProps {
  objective: Objective;
  children?: React.ReactNode;
  handleComments: () => void;
}
const ObjectiveItem = observer((props: ObjectiveItemProps) => {
  const { objective, handleComments, children } = props;
  const { weight, description, perspective } = objective.asJson;
  const { rate, isUpdated } = objective.q2Rating;

  return (
    <ErrorBoundary>
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4 flex-1">
            <div className="flex-shrink-0">
              <Rating rate={rate} isUpdated={isUpdated} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[#3f6a8e] mb-1">
                {description}
              </h3>
              <div className="flex items-center gap-3 text-sm">
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-[#3f6a8e] font-medium">
                  {fullPerspectiveName(perspective)}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
                  Weight: {weight || 0}%
                </span>
              </div>
            </div>
          </div>
          <button
            title="Comments"
            className="flex items-center gap-2 px-4 py-2 text-[#84cbe9] hover:bg-blue-50 rounded-lg transition-colors"
            onClick={handleComments}
          >
            <span data-uk-icon="icon: comment; ratio: 0.9" />
            <span className="text-sm font-medium">Comments</span>
          </button>
        </div>

        <div className="mt-4">{children}</div>
      </div>
    </ErrorBoundary>
  );
});

interface IRatingProps {
  measures: Measure[];
}
const ScorecardRatings = observer((props: IRatingProps) => {
  const { measures } = props;
  const { api, store } = useAppContext();
  const { uid } = useParams();

  const objectives = store.objective.all
    .filter((obj) => obj.asJson.uid === uid)
    .map((obj) => {
      return obj.asJson;
    });
  const scorecardMetaData = store.companyScorecardMetadata.all.find(
    (m) => m.asJson.uid === uid
  )?.asJson;

  const $measures = measures.map((measure) => measure.asJson);

  const rating1 = semester1EmpRating($measures);
  const rating2 = semester1SuperRating($measures);
  const rating3 = semester1FinalRating($measures);

  const q2_e_css = rateColor(rating1, true);
  const q2_s_css = rateColor(rating2, true);
  const q2_f_css = rateColor(rating3, true);

  useEffect(() => {
    const loadData = async () => {
      await api.companyScorecardMetadata.getAll();
    };
    loadData();
  }, []);

  return (
    <ErrorBoundary>
      <div className="measure-rating-table">
        <table>
          <thead className="header">
            <tr>
              <th>E-Rating</th>
              <th>S-Rating</th>
              <th>F-Rating</th>
            </tr>
          </thead>
          <tbody>
            <tr className="row">
              <td className={`value ${q2_e_css}`}>{rating1.toFixed(2)}</td>
              <td className={`value ${q2_s_css}`}>{rating2.toFixed(2)}</td>
              <td className={`value ${q2_f_css}`}>{rating3.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </ErrorBoundary>
  );
});

interface IObjectivesProps {
  tab: string;
  objectives: Objective[];
  canUpdate: boolean;
  midtermRated: boolean;
}

const ObjectivesList = observer((props: IObjectivesProps) => {
  const { store } = useAppContext();
  const { tab, objectives, canUpdate, midtermRated } = props;

  const handleComments = (objective: Objective) => {
    store.objective.select(objective.asJson);
    showModalFromId(MODAL_NAMES.EXECUTION.OBJECTIVE_Q2_COMMENT_MODAL);
  };

  const _objectives = useMemo(() => {
    return tab === ALL_TAB.id
      ? objectives
      : objectives.filter((o) => o.asJson.perspective === tab);
  }, [store.objective, tab, objectives]);

  return (
    <ErrorBoundary>
      <div className="objective-table uk-margin">
        <ErrorBoundary>
          {_objectives.map((objective) => (
            <ObjectiveItem
              handleComments={() => handleComments(objective)}
              key={objective.asJson.id}
              objective={objective}
            >
              <MeasureTable
                measures={objective.measures}
                canUpdate={canUpdate}
                midtermRated={midtermRated}
              />
            </ObjectiveItem>
          ))}
        </ErrorBoundary>
        <ErrorBoundary>
          {!objectives.length && (
            <EmptyError errorMessage="No objective found" />
          )}
        </ErrorBoundary>
      </div>
    </ErrorBoundary>
  );
});

interface IQ2Props {
  agreement: IScorecardMetadata;
  selectedUser: IUser;
  currentUser: IUser;
  measures: Measure[];
  objectives: Objective[];
}

const EmployeeQ2ReviewCycle = observer((props: IQ2Props) => {
  const { store, ui, api } = useAppContext();
  const [tab, setTab] = useState(ALL_TAB.id);

  const { agreement, selectedUser, currentUser, measures, objectives } = props;
  const midtermRated = agreement.midtermRated;

  const isDisabled = useMemo(
    () => !(agreement.quarter2Review.status === "submitted"),
    [agreement.quarter2Review.status]
  );

  const canUpdate = useMemo(
    () => agreement.quarter2Review.status === "submitted",
    [agreement]
  );

  const incompleteReviewError = measures.some(
    (m) => m.asJson.supervisorRating === null
  );

  const onSubmit = async () => {
    if (incompleteReviewError) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "In complete review. Some measures are not rated.",
        timeoutInMs: 10000,
      });
      return;
    } else if (!selectedUser || !currentUser) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "User not found, email not sent.",
        timeoutInMs: 10000,
      });
      return;
    }

    const { MY_SUBJECT, MY_BODY } = MAIL_MIDTERM_RATINGS_ME(
      selectedUser.displayName,
      currentUser.displayName
    );

    const $agreement: IScorecardMetadata = { ...agreement, midtermRated: true };
    await api.individualScorecard.update($agreement);
    await api.mail.sendMailCC(
      [selectedUser.email!],
      [currentUser.email!],
      MAIL_EMAIL,
      MY_SUBJECT,
      MY_BODY
    );
    ui.snackbar.load({
      id: Date.now(),
      type: "success",
      message: "Email sent.",
      timeoutInMs: 10000,
    })
  }


  if (agreement.quarter2Review.status === "pending" || agreement.quarter2Review.status === "in-progress")
    return (
      <ErrorBoundary>
        <NoPerformanceData title="Midterm progress not submitted." />
      </ErrorBoundary>
    );

  return (
    <ErrorBoundary>
      <div className="scorecard-page uk-section uk-section-small">
        <div className="uk-container uk-container-xlarge">
          <ErrorBoundary></ErrorBoundary>
          <ErrorBoundary>

          </ErrorBoundary>
          <ErrorBoundary>
            <ObjectivesList tab={tab} objectives={objectives} canUpdate={canUpdate} midtermRated={midtermRated} />
          </ErrorBoundary>
          <ErrorBoundary>
            <ScorecardRatings measures={measures} />
          </ErrorBoundary>
        </div>
      </div>
    </ErrorBoundary>
  );
});

export default EmployeeQ2ReviewCycle;
